import { useState, useEffect } from "react";
import { Calendar, MapPin, Clock, Users, Minus, Plus, ShoppingCart, Check, Ticket, Heart, Star, ExternalLink, Phone, Flower2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

import { API } from "../config/api";

// ============================================================================
// ONAM 2026 - Sri Krishna Jayanthi & Onam Celebration
// ============================================================================

// TICKET SALES - Set to true to disable purchases
const TICKETS_DISABLED = true;
const TICKETS_DISABLED_MESSAGE = "Ticket sales are currently closed. Thank you for your interest!";

// Category definitions with prices
const ONAM_CATEGORIES = [
  { id: "A", name: "Adults", description: "Ages 13 & Above", code: "adult", price: 19 },
  { id: "B", name: "Youth", description: "Ages 6-12", code: "youth", price: 15 },
  { id: "C", name: "Child", description: "5 and under", code: "child", price: 0 },
];

// Mega Sponsor Info
const MEGA_SPONSOR = {
  name: "MOHANDAS",
  title: "Kalarikal Manikandadas (Realtor)",
  phone: "647 542 1000",
  website: "www.realtormohandas.ca"
};

export default function OnamEventPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, []);

  const fetchEventData = async () => {
    try {
      const eventRes = await fetch(`${API}/events/onam-2026`);
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      } else {
        setEvent({
          title: "Sri Krishna Jayanthi & Onam Celebration 2026",
          slug: "onam-2026",
          description: "Celebrate Onam with our tradition - Pookkalam, Thiruvathira, Onakkalikal & Grand Onasadhya",
          date_time: "2026-09-06T10:00:00",
          venue: "1424 Clark Road",
          address: "London, ON",
          maps_url: "https://www.google.com/maps/search/?api=1&query=1424+Clark+Road+London+ON"
        });
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      setEvent({
        title: "Sri Krishna Jayanthi & Onam Celebration 2026",
        slug: "onam-2026",
        description: "Celebrate Onam with our tradition - Pookkalam, Thiruvathira, Onakkalikal & Grand Onasadhya",
        date_time: "2026-09-06T10:00:00",
        venue: "1424 Clark Road",
        address: "London, ON",
        maps_url: "https://www.google.com/maps/search/?api=1&query=1424+Clark+Road+London+ON"
      });
    } finally {
      setLoading(false);
    }
  };

  const getTicketPrice = (categoryId) => {
    const category = ONAM_CATEGORIES.find(c => c.id === categoryId);
    return category ? category.price : 0;
  };

  const getTotalPrice = () => {
    let total = 0;
    Object.entries(cart).forEach(([categoryId, qty]) => {
      const price = getTicketPrice(categoryId) * 100;
      total += (price * qty);
    });
    return total;
  };

  const getTotalTickets = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  };

  const updateCart = (categoryId, delta) => {
    setCart(prev => {
      const current = prev[categoryId] || 0;
      const newQty = Math.max(0, Math.min(current + delta, 20));
      if (newQty === 0) {
        const { [categoryId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [categoryId]: newQty };
    });
  };

  const handleCheckout = async () => {
    if (TICKETS_DISABLED) {
      toast.error("Ticket sales are currently closed");
      return;
    }
    if (getTotalTickets() === 0) {
      toast.error("Please select at least one ticket");
      return;
    }
    if (!customerInfo.name || !customerInfo.email) {
      toast.error("Please enter your name and email");
      return;
    }

    setCheckingOut(true);

    try {
      const ticketsData = [];
      Object.entries(cart).forEach(([categoryId, qty]) => {
        if (qty > 0) {
          const category = ONAM_CATEGORIES.find(c => c.id === categoryId);
          const ticketPrice = getTicketPrice(categoryId);
          ticketsData.push({
            slot_id: "ONAM",
            slot_name: "Onam 2026",
            slot_time: "10:00 AM - 4:00 PM",
            category_id: categoryId,
            category_name: category?.name || "Ticket",
            description: category?.description || "",
            price_cents: ticketPrice * 100,
            quantity: qty
          });
        }
      });

      let eventId = event?.id;
      if (!eventId) {
        const evRes = await fetch(`${API}/events/onam-2026`);
        if (evRes.ok) {
          const evData = await evRes.json();
          eventId = evData.id;
        }
      }

      const response = await fetch(`${API}/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: eventId || "onam-2026",
          tickets_data: ticketsData,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone || "",
          origin_url: window.location.origin,
          total_amount: getTotalPrice()
        })
      });

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Failed to create checkout session");
        setCheckingOut(false);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50" data-testid="onam-event-page">
      {/* Hero Section with Onam Flyer */}
      <section className="relative" style={{ backgroundColor: "#0F392B" }}>
        {/* Full-width Onam Flyer Banner */}
        <div className="w-full">
          <img 
            src="/onam-ticket-flyer.jpg" 
            alt="Sri Krishna Jayanthi & Onam Celebration 2026" 
            className="w-full h-auto max-h-[70vh] mx-auto object-contain"
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Ticket Selection */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-kerala-green text-white p-6 flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Ticket className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-semibold">
                      {TICKETS_DISABLED ? "Ticket Sales Closed" : "Select Your Tickets"}
                    </h2>
                    <p className="text-white/80 text-sm">
                      {TICKETS_DISABLED ? "Thank you for your interest" : "Choose the number of tickets per category"}
                    </p>
                  </div>
                </div>

                {TICKETS_DISABLED ? (
                  <div className="p-8 text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Ticket className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="font-heading text-xl text-gray-700 mb-2">Ticket Sales Closed</h3>
                    <p className="text-gray-500 max-w-md mx-auto">{TICKETS_DISABLED_MESSAGE}</p>
                  </div>
                ) : (
                  <div className="p-6 space-y-4">
                    {ONAM_CATEGORIES.map((category) => {
                      const qty = cart[category.id] || 0;
                      const priceDisplay = category.price > 0 ? `$${category.price.toFixed(2)}` : "FREE";
                      
                      return (
                        <div 
                          key={category.id}
                          className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                            qty > 0 ? "border-kerala-green bg-kerala-green/5" : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`ticket-category-${category.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                              category.price === 0 ? "bg-green-100 text-green-700" : "bg-kerala-gold/20 text-kerala-green"
                            }`}>
                              {category.id}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{category.name}</h3>
                              <p className="text-gray-500 text-sm">{category.description} - <span className="font-semibold text-kerala-green">{priceDisplay}</span></p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateCart(category.id, -1)}
                              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-50"
                              disabled={qty === 0}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-bold text-lg">{qty}</span>
                            <button
                              onClick={() => updateCart(category.id, 1)}
                              className="w-10 h-10 rounded-full bg-kerala-green text-white hover:bg-kerala-green/90 flex items-center justify-center transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="bg-gray-50 p-4 border-t border-gray-100">
                  <div className="flex items-start gap-3 text-sm text-gray-600">
                    <MapPin className="w-5 h-5 text-kerala-green flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{event?.venue || "1424 Clark Road"}</p>
                      <p>{event?.address || "London, ON"}</p>
                      <a 
                        href={event?.maps_url || "https://www.google.com/maps/search/?api=1&query=1424+Clark+Road+London+ON"} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-kerala-green hover:underline inline-flex items-center gap-1 mt-1"
                      >
                        View on Google Maps <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 sticky top-24">
                <div className="bg-kerala-green text-white p-4 flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5" />
                  <h3 className="font-heading font-semibold">Order Summary</h3>
                </div>

                <div className="p-6">
                  {getTotalTickets() === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Ticket className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500">No tickets selected</p>
                      <p className="text-gray-400 text-sm mt-1">Choose your tickets to continue</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(cart).map(([categoryId, qty]) => {
                        if (qty === 0) return null;
                        const category = ONAM_CATEGORIES.find(c => c.id === categoryId);
                        const ticketPrice = getTicketPrice(categoryId);
                        const lineTotal = ticketPrice * qty;
                        
                        return (
                          <div key={categoryId} className="flex justify-between items-start pb-3 border-b border-gray-100">
                            <div>
                              <p className="font-medium text-gray-900">{category?.name}</p>
                              <p className="text-xs text-gray-400">{qty} × ${ticketPrice.toFixed(2)}</p>
                            </div>
                            <p className="font-semibold text-kerala-green">
                              {lineTotal > 0 ? `$${lineTotal.toFixed(2)}` : "FREE"}
                            </p>
                          </div>
                        );
                      })}

                      <div className="flex justify-between items-center pt-2">
                        <span className="font-semibold text-gray-900">Total</span>
                        <span className="font-heading text-2xl text-kerala-green">${(getTotalPrice() / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {getTotalTickets() > 0 && !TICKETS_DISABLED && (
                    <div className="mt-6 pt-6 border-t border-gray-100 space-y-4">
                      <h4 className="font-semibold text-gray-900">Your Details</h4>
                      
                      <div>
                        <Label htmlFor="name" className="text-sm text-gray-600">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="Enter your full name"
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email" className="text-sm text-gray-600">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone" className="text-sm text-gray-600">Phone (Optional)</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="555-123-4567"
                          value={customerInfo.phone}
                          onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                          className="mt-1"
                        />
                      </div>

                      <Button 
                        className="w-full btn-primary text-lg py-6 mt-4"
                        onClick={handleCheckout}
                        disabled={checkingOut || !customerInfo.name || !customerInfo.email}
                      >
                        {checkingOut ? (
                          <>
                            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Proceed to Payment
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-gray-400 text-center">
                        Secure payment powered by Stripe
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-8 bg-kerala-cream">
        <div className="section-container">
          <div className="bg-white rounded-xl p-6 shadow-md flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-kerala-green/10 p-3 rounded-full">
                <Heart className="w-6 h-6 text-kerala-green" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-kerala-green">Support This Event</h3>
                <p className="text-gray-600 text-sm">Help make Onam 2026 unforgettable</p>
              </div>
            </div>
            <Link to="/sponsors">
              <Button className="btn-secondary">Become a Sponsor</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
